import { useCallback, useEffect, useState } from 'react';

import Checkbox from '@cobliteam/ui/form/Checkbox';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Label from '@cobliteam/ui/form/Label';

import type { MediaRequestStatusFilter } from '@cobliteam/common-types';

import type { Trigger } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/triggers';

import { useInfinityTriggers } from '@cobliteam/hooks/src/useEntity/useAlerts';

import Icon from '@cobliteam/ui/icons';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';

export interface MultiSelectTriggerNameProps {
  isOpen?: boolean;
  size?: 'condensed' | 'regular';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
  requestVideo?: boolean;
}

const MultiSelectTriggerName = ({ isOpen, size = 'regular', selectedOptions = [], onChange, requestVideo }: MultiSelectTriggerNameProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const filters = {
    page: 0,
    size: 1000,
    requestVideo,
  };

  const { data, fetchNextPage, isLoading, hasNextPage, isFetching } = useInfinityTriggers(filters);

  // check if all selected types are in draft
  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const triggersFlatted = data?.pages.flatMap((page) => page.content) || [];

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = draft.find((d) => d === type);
    const updatedSelected = isSelected ? draft.filter((d) => d !== type) : [...draft, type];
    setDraft(updatedSelected as MediaRequestStatusFilter[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
    setSearch(undefined);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const sortByName = (a: Trigger, b: Trigger) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  };

  const sortBySelected = useCallback(
    (a: Trigger, b: Trigger) => {
      if (selectedOptions.find((g) => g === a.id) && !selectedOptions.find((g) => g === b.id)) return -1;
      if (!selectedOptions.find((g) => g === a.id) && selectedOptions.find((g) => g === b.id)) return 1;
      return 0;
    },
    [selectedOptions]
  );

  const dataFiltered = triggersFlatted
    ?.filter((trigger: Trigger) => {
      if (!search) return true;
      return trigger.name.toLowerCase().includes(search.toLowerCase());
    })
    .sort(sortByName)
    .sort(sortBySelected);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='text' size='condensed' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-5'>
            <InputSearch placeholder='Procure pelo nome' value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='ml-5'>
            <Dropdown.ListPageable
              heightList={300}
              heightItem={32}
              data={dataFiltered}
              hasNextPage={hasNextPage}
              isFetching={isFetching}
              placeholderComponent={<Dropdown.Placeholder />}
              isLoading={isLoading}
              loadingComponent={<Dropdown.Loading />}
              fetchNextPage={fetchNextPage}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.id} key={item.id} checked={draft.includes(item.id)} onCheckedChange={() => onChangeSelect(item.id)} />
                  <Label htmlFor={item.id} className='line-clamp-1 w-full pr-4'>
                    {item.name}
                  </Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button full variant='text' size='condensed' onClick={handleClose}>
                Cancelar
              </Button>
              <Button full variant='contained' size='condensed' color='primary' onClick={handleApply} disabled={hasSameElements}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<Icon name='file-text' />}>
        Regra solicitante {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectTriggerName;
